import { faScanner, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import Button from '@ssg/common/Components/Button';
import React from 'react';
import { MOVABLE_PLACEMENT_PREFIX } from './movablePrefixes';
import { ScannerProps } from './MovablesTabs';
import BarcodeScanner from './BarcodeScannerTwo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@ssg/common/Components/Modal';
import { useGetMovablesQuery } from '@ssg/common/GraphQL/indexV2';
import Table from '@ssg/common/Components/Table';
import GraphQLExtensionsElement from '@ssg/common/Components/GraphQLExtensionsElement';
import TotalCount from '@ssg/common/Components/TotalCount';
import { GraphQLExtensionsData } from '@ssg/common/Components/GraphQLExtensionsContext';
import { useTranslation } from 'react-i18next';

const FETCH_LIMIT = 20;

const MovablePlacementScan = () => {
	const [showScannerModal, setShowScannerModal] = React.useState<ScannerProps>({ mode: 'HIDE', title: '' });
	const [scannedId, setScannedId] = React.useState<string>();

	React.useEffect(() => {
		if (showScannerModal.mode === 'HIDE') {
			setScannedId(undefined);
		}
	}, [showScannerModal.mode]);
	return (
		<>
			<Button
				primary
				text="common.scanPlacement"
				icon={faScanner}
				onClick={() => {
					setScannedId(undefined);
					setShowScannerModal({
						mode: 'SINGLE',
						title: 'common.scanPlacement',
						allowedPrefixes: [MOVABLE_PLACEMENT_PREFIX],
					});
				}}
				className="mr-4 mb-3"
			/>
			<Modal
				title={showScannerModal.title}
				close={() => setShowScannerModal({ mode: 'HIDE', title: '' })}
				visible={showScannerModal.mode !== 'HIDE'}
				body={
					scannedId ? (
						<MovablesOnPlacement placementId={scannedId.replace('p:', '')} />
					) : (
						<BarcodeScanner setScannedId={setScannedId} multiple={showScannerModal.mode === 'MULTIPLE'} allowedPrefixes={showScannerModal.allowedPrefixes} />
					)
				}
			/>
		</>
	);
};

const MovablesOnPlacement = ({ placementId }: { placementId: string }) => {
	const { t } = useTranslation();
	const [offset, setOffset] = React.useState(0);
	const { data, loading, fetchMore } = useGetMovablesQuery({
		variables: {
			placement: placementId,
			offset: offset,
			limit: FETCH_LIMIT,
		},
		fetchPolicy: 'cache-and-network',
	});
	const movables = React.useMemo(() => data?.movables || [], [data]);
	const [fetchMoreLoading, setFetchMoreLoading] = React.useState(false);

	const movablesTotalCountName = 'movablesTotalCount';
	const graphQLExtensionsData = React.useContext(GraphQLExtensionsData);
	const totalMovables = React.useMemo(() => (graphQLExtensionsData[movablesTotalCountName] as number | undefined | null) ?? 0, [graphQLExtensionsData]);
	if (loading && !data)
		return (
			<div className="my-2 py-2 text-center">
				<FontAwesomeIcon size="3x" icon={faSpinner} className="animate-spin" />
			</div>
		);
	return (
		<>
			<GraphQLExtensionsElement
				name={movablesTotalCountName}
				render={value => <TotalCount totalCount={value as number | undefined | null} loading={loading} quantityText={t('common.quantity')} entityText={t('common.movables')} />}
			/>
			<Table
				data={movables}
				keySelector={m => m.id}
				noDataFoundText="movable.noMovablesFound"
				columns={[
					{
						label: 'common.case',
						selectFn: m => m.case.erpNo,
						sortFn: (a, b) => a.case.erpNo.localeCompare(b.case.erpNo),
					},
					{
						label: 'common.description',
						selectFn: m => m.description,
						sortFn: (a, b) => a.description.localeCompare(b.description),
					},
					{
						label: 'movable.volume',
						selectFn: m => <span>{m.volume}&nbsp;m&sup3;</span>,
						numeric: true,
						sortFn: (a, b) => a.volume - b.volume,
					},
					{
						label: 'common.debitor',
						selectFn: m => m.case.debitor.company,
						sortFn: (a, b) => a.case.debitor.company.localeCompare(b.case.debitor.company),
					},
					{
						label: 'common.status',
						selectFn: m => m.status,
						sortFn: (a, b) => a.status.localeCompare(b.status),
					},
				]}
			/>
			{typeof data !== 'undefined' && totalMovables > movables.length && (
				<Button
					fullWidth
					text="common.getMore"
					secondary
					loading={fetchMoreLoading}
					className="mt-2"
					onClick={async () => {
						setOffset(movables.length);
						setFetchMoreLoading(true);
						await fetchMore({
							variables: {
								offset: movables.length,
							},
						});
						setFetchMoreLoading(false);
					}}
				/>
			)}
		</>
	);
};

export default MovablePlacementScan;

